import type { Security } from '../../types';
import { getSearchTerms } from '../../utils/findSubstringMatches';
import { getSecurityIdentifierValues } from '../../utils/getSecurityMetadata';
import { getSearchSymbol } from '../../utils/security';
import type { SortFilterItem } from '../Form/Autocomplete/types';

/** Selector tiers stay coarser than substring match quality: contains covers ends-with. */
const SelectorTier = {
  Exact: 0,
  StartsWith: 1,
  Contains: 2,
  Description: 3,
} as const;

type NormalizedSecuritySearch = {
  identifiers: readonly string[];
  searchSymbol: string;
  symbol: string;
};

/**
 * SymbolSelector filter and sort implementation.
 * Ranks by tier (exact > starts-with > label/Symbol/searchSymbol contains > description-only).
 * With several terms, each term's tier is summed and the lower sum ranks first, so a starts-with
 * plus a contains (3) beats two contains (4). Equal sums then use recent-symbols > Rank > alphabetical.
 *
 * Matches DisplaySymbol (label), raw Symbol, and searchSymbol (hyphen-stripped lowercase
 * Symbol, e.g. "ethusd" for "ETH-USD") so Kalshi-style IDs and hyphenless queries both work.
 *
 * Uses score buckets instead of wrapping each item in {item, score} — at large
 * securities counts this avoids hundreds of thousands of small object allocations
 * per keystroke. Normalized symbol text is cached on the security for the lifetime of this filter.
 */
export function createSortFilterOverride(recentSymbolsList: string[], minMatchCharLength = 2, includeSedol = false) {
  const recentIndexMap = new Map(
    recentSymbolsList
      .slice(0, 5)
      .reverse()
      .map((symbol, idx) => [symbol, idx] as const)
  );
  const collator = new Intl.Collator();
  const normalizedBySecurity = new WeakMap<Security, NormalizedSecuritySearch>();

  const getNormalized = (security: Security): NormalizedSecuritySearch => {
    const cached = normalizedBySecurity.get(security);
    if (cached != null) {
      return cached;
    }

    const normalized = {
      identifiers: getSecurityIdentifierValues(security, includeSedol).map(identifier => identifier.toLowerCase()),
      searchSymbol: security.searchSymbol ?? getSearchSymbol(security.Symbol),
      symbol: security.Symbol.toLowerCase(),
    };
    normalizedBySecurity.set(security, normalized);
    return normalized;
  };

  const tiebreaker = (a: SortFilterItem<Security>, b: SortFilterItem<Security>): number => {
    const aRecent = recentIndexMap.get(a.item.Symbol) ?? -1;
    const bRecent = recentIndexMap.get(b.item.Symbol) ?? -1;
    if (aRecent > -1 || bRecent > -1) {
      return bRecent - aRecent;
    }
    if (a.item.Rank != null && b.item.Rank != null) {
      return a.item.Rank - b.item.Rank;
    }
    if (a.item.DisplaySymbol != null && b.item.DisplaySymbol != null) {
      return collator.compare(a.item.DisplaySymbol, b.item.DisplaySymbol);
    }
    return 0;
  };

  const matchTier = (
    item: SortFilterItem<Security>,
    term: string,
    normalized: NormalizedSecuritySearch
  ): number | undefined => {
    const { identifiers, searchSymbol, symbol } = normalized;
    if (term === item.lowerLabel || term === symbol || term === searchSymbol || identifiers.includes(term)) {
      return SelectorTier.Exact;
    }
    if (
      item.lowerLabel.startsWith(term) ||
      symbol.startsWith(term) ||
      searchSymbol.startsWith(term) ||
      identifiers.some(identifier => identifier.startsWith(term))
    ) {
      return SelectorTier.StartsWith;
    }
    if (
      item.lowerLabel.includes(term) ||
      symbol.includes(term) ||
      searchSymbol.includes(term) ||
      identifiers.some(identifier => identifier.includes(term))
    ) {
      return SelectorTier.Contains;
    }
    if (item.lowerDescription.includes(term)) {
      return SelectorTier.Description;
    }
    return undefined;
  };

  return (fullList: SortFilterItem<Security>[], searchText: string): SortFilterItem<Security>[] => {
    // Below minMatchCharLength skips filter/sort entirely — fullList already arrives sorted by recency/Rank from the caller.
    // Keep the existing ordered list for short queries. Every remaining term has to match.
    const terms = getSearchTerms(searchText, minMatchCharLength);
    if (terms.length === 0) {
      return fullList;
    }

    const buckets: SortFilterItem<Security>[][] = [];
    for (let score = 0; score <= terms.length * SelectorTier.Description; score++) {
      buckets.push([]);
    }

    for (const item of fullList) {
      const normalized = getNormalized(item.item);
      let score = 0;
      for (const term of terms) {
        const tier = matchTier(item, term, normalized);
        if (tier == null) {
          score = -1;
          break;
        }
        score += tier;
      }
      if (score < 0) {
        continue;
      }
      buckets[score].push(item);
    }

    return buckets.flatMap(bucket => bucket.toSorted(tiebreaker));
  };
}
